'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { Suspense, useEffect, useState } from 'react';
import type { IAtVideoPlayerProps } from './at-video-player.types';

const ReactPlayer = dynamic(() => import('react-player'), {
  ssr: false,
  loading: () => (
    <div
      className="absolute inset-0 animate-pulse bg-gray-100"
      aria-label="Loading video player..."
    />
  ),
});

/**
 * `AtVideoPlayer` is a wrapper around `ReactPlayer` for embedding responsive videos.
 *
 * Features:
 * - Supports all ReactPlayer props (controls, playing, muted, onEnded, etc.)
 * - Responsive container with consistent styling
 * - Optimized for both background videos and featured video use cases
 * - Client-side only rendering for better performance
 */
export const AtVideoPlayer = ({
  containerClasses,
  url,
  onReady,
  ...reactPlayerProps
}: IAtVideoPlayerProps) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Show loading state until component has mounted on client
  if (!hasMounted) {
    return (
      <div className={classNames('relative', containerClasses)}>
        <div
          className="absolute inset-0 animate-pulse bg-gray-100"
          aria-label="Loading video player..."
        />
      </div>
    );
  }

  return (
    <div className={classNames(containerClasses)}>
      <Suspense fallback={null}>
        <ReactPlayer
          aria-label="Video Player"
          className="absolute left-0 top-0"
          data-testid="video-player"
          height="100%"
          width="100%"
          playsinline
          url={url}
          onReady={onReady}
          {...reactPlayerProps}
        />
      </Suspense>
    </div>
  );
};
